<script lang="ts" setup>
    const selectedShippingAddress = defineModel<string>('selectedShippingAddress');
    const selectedBillingAddress = defineModel<string>('selectedBillingAddress');
    const customShipping = defineModel<boolean>('customShipping');

    const { isLoggedIn, isGuestSession } = useShopwareUser();
    const { customerAddresses, loadCustomerAddresses } = useAddress();
    const { auth } = useLoginState();
    const selfServiceUrlAddresses = useSelfServiceUrl('addresses');
    const { isVirtualCart } = useCart();

    const isLoading = ref<boolean>();

    const refreshAddresses = async () => {
        isLoading.value = true;
        await loadCustomerAddresses();
        isLoading.value = false;
    };

    const checkCustomShipping = () => {
        if (customShipping.value === true) {
            selectedShippingAddress.value = selectedBillingAddress.value;
        }
    };

    const addAddressModalController = useModal();
</script>
<template>
    <div>
        <SharedModal :controller="addAddressModalController">
            <SharedAccountAddressForm
                @success="
                    refreshAddresses();
                    addAddressModalController.close();
                "
            />
        </SharedModal>
        <fieldset class="info-element">
            <div>
                <div class="addresses-edit">
                    <legend>
                        {{ $t('checkout.billingAddressLabel') }}
                    </legend>

                    <button
                        v-if="(isLoggedIn || isGuestSession) && auth"
                        class="fake-link"
                        @click="() => navigateTo(selfServiceUrlAddresses, { external: true })"
                    >
                        {{ $t('checkout.editAddresses') }}
                    </button>
                </div>
                <div v-if="isLoggedIn || isGuestSession">
                    {{ $t('checkout.selectBillingAddress') }}
                </div>
                <div v-else>Deine Adresse kann erst angegeben werden nach Login oder Gastlogin</div>
            </div>
            <template v-if="!isLoading">
                <div
                    v-for="address in customerAddresses.filter(
                        (a) => a.street.trim().length > 0 && a.street.trim() !== '-'
                    )"
                    :key="address.id"
                    class="radio-container align-top"
                >
                    <input
                        :id="`billing-${address.id}`"
                        v-model="selectedBillingAddress"
                        :value="address.id"
                        name="billing-address"
                        type="radio"
                        :data-testid="`checkout-billing-address-${address.id}`"
                    />
                    <label
                        class="fix-label"
                        :for="`billing-${address.id}`"
                    >
                        <AccountAddressCard
                            :key="address.id"
                            :address="address"
                            :can-set-default="false"
                            @success="refreshAddresses()"
                        />
                    </label>
                </div>
            </template>
            <button
                v-if="!auth && (isLoggedIn || isGuestSession)"
                type="button"
                data-testid="checkout-add-new-billing-address-button"
                class="btn-text"
                @click="addAddressModalController.open"
            >
                {{ $t('checkout.addNewBillingAddress') }}
            </button>
            <template v-if="!isVirtualCart && !isLoading">
                <div
                    v-if="isLoggedIn || isGuestSession"
                    class="checkbox-wrapper"
                >
                    <input
                        id="customShipping"
                        v-model="customShipping"
                        data-testid="checkout-custom-shipping-address-checkbox"
                        name="privacy"
                        type="checkbox"
                        @click="checkCustomShipping"
                    />

                    <label
                        for="customShipping"
                        class="field-label"
                    >
                        {{ $t('checkout.differentBillingAddress') }}
                    </label>
                </div>

                <div
                    v-if="customShipping"
                    id="custom-shipping"
                >
                    <div
                        v-for="address in customerAddresses.filter(
                            (a) => a.street.trim().length > 0 && a.street.trim() !== '-'
                        )"
                        :key="address.id"
                        class="radio-container align-top"
                    >
                        <input
                            :id="`shipping-${address.id}`"
                            v-model="selectedShippingAddress"
                            :value="address.id"
                            name="shipping-address"
                            type="radio"
                            :data-testid="`checkout-shipping-address-${address.id}`"
                        />
                        <label
                            :for="`shipping-${address.id}`"
                            :class="{
                                'animate-pulse': isLoading
                            }"
                        >
                            <AccountAddressCard
                                :key="address.id"
                                :address="address"
                                :can-set-default="false"
                                @success="refreshAddresses()"
                            />
                        </label>
                    </div>
                    <button
                        v-if="!auth && (isLoggedIn || isGuestSession)"
                        type="button"
                        data-testid="checkout-add-new-shipping-address-button"
                        class="btn-text"
                        @click="addAddressModalController.open"
                    >
                        {{ $t('checkout.addNewShippingAddress') }}
                    </button>
                </div>
            </template>
        </fieldset>
    </div>
</template>
